import { Search } from '@assets/svgs';
import { Autocomplete } from '@components/autocomplete';
import { Select } from '@components/select';
import { TextField } from '@components/textField';
import { ToastType } from '@components/toast';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { AddressValidationStatus } from '@models/customer';
import { AddressInput, EnteredAddress } from '@models/validation';
import { AutocompleteRenderInputParams, Grid, Typography } from '@mui/material';
import { InputValidation } from '@pages/NewQuote/location';
import { ExternalIcon } from '@pages/NewQuote/location/ExternalIcon';
import { NoMatchingAddress } from '@pages/NewQuote/location/NoMatchingAddress';
import { MessageNoMatching } from '@pages/NewQuote/namedInsured/MessageNoMatching';
import { useAddressSuggestions } from '@services/smarty';
import { useValidPhysicalAddress } from '@services/validation';
import { formatAddress, initialAddressState } from '@utils/address';
import { states } from '@utils/states';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type TBusinessAddressProps = {
  isNewCustomer: boolean;
  address: EnteredAddress;
  setIsLoading?: (l: boolean) => void;
  updateAddress: (addressToUpdate?: AddressInput) => void;
  handleConfirmNewCustomer: (addressToUpdate: AddressInput) => void;
  isBusinessTypeMissing: boolean;
  disabled?: boolean;
  isSpecialEvent?: boolean;
  skipValidation?: boolean;
};
export const BusinessAddress = ({
  isNewCustomer,
  address,
  setIsLoading,
  updateAddress,
  isBusinessTypeMissing,
  handleConfirmNewCustomer,
  disabled,
  isSpecialEvent,
  skipValidation = false,
}: TBusinessAddressProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showNoMatchingAddress, setShowNoMatchingAddress] = useState<boolean>(false);
  const anchorRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState<AddressInput>(address);
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const [addressLookupValue, setAddressLookupValue] = useState<{
    address: AddressInput;
    showModal?: boolean;
  } | null>();

  useEffect(() => {
    // deep compare address and selectedValue to avoid unnecessary updates
    const isSame = Object.keys(address).every((key) => {
      const addressKey = key as keyof EnteredAddress;
      const selectedKey = key as keyof AddressInput;
      return address[addressKey] === selectedValue[selectedKey];
    });

    if (!isSame) {
      setSelectedValue(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const [inputValue, setInputValue] = useState<string>('');
  const { addressSuggestions } = useAddressSuggestions(inputValue);
  const { isFetching, validPhysicalAddress, refetchAddress, refetch, error } = useValidPhysicalAddress(
    addressLookupValue?.address || initialAddressState,
  );
  const validationStatus = validPhysicalAddress?.status;

  const debouncedInputValue = useDebouncedCallback((value) => {
    setInputValue(value);
  }, DEBOUNCE_DELAYS.TYPING);

  useEffect(() => {
    if (skipValidation && addressLookupValue) {
      const addressToUse = address || addressLookupValue?.address || selectedValue;
      // If validation should be skipped, immediately confirm the address
      if (isNewCustomer) {
        handleConfirmNewCustomer(addressToUse);
      } else {
        setSelectedValue(addressToUse);
        updateAddress(addressToUse);
      }
      return;
    }

    if (addressLookupValue && addressLookupValue.showModal) {
      refetchAddress().then((response) => {
        if (response.isSuccess) {
          const { status } = response.data;
          if (!status.includes(AddressValidationStatus.ExactMatch)) {
            handlePopoverOpen();
          } else if (isNewCustomer) {
            handleConfirmNewCustomer(selectedValue);
          } else {
            updateAddress(selectedValue);
          }
        } else if (response.error && address) {
          console.warn('Error fetching address', response.error, 'Address: ');
        }
      });
    } else if (addressLookupValue && !validationStatus) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressLookupValue, validationStatus, refetch, skipValidation]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        persist: true,
        variant: ToastType.Error,
      });
    }

    setIsLoading?.(isFetching || !!error);
  }, [isFetching, error, setIsLoading, enqueueSnackbar]);

  const handleAddressConfirm = (address: any) => {
    handlePopoverClose();
    if (isNewCustomer) {
      handleConfirmNewCustomer(address);
    } else {
      updateAddress(address);
    }
  };
  const handleBusinessAddressChange = (event: SyntheticEvent | any, name: string) => {
    const value = (event.target as HTMLInputElement).value;
    const updatedAddress = { ...selectedValue, [name]: value };
    setSelectedValue(updatedAddress);
    // Only update the parent's state without triggering verification
    updateAddress(updatedAddress);
  };

  const handleInvalidAddress = () => {
    setSelectedValue({ ...selectedValue, isAddressValid: false });
  };
  const handleNoMatchingAddress = async () => {
    handlePopoverChange();
    setAddressLookupValue({ address: selectedValue, showModal: true });
  };
  const handlePopoverChange = () => {
    setAnchorEl(anchorRef.current);
  };

  const handlePopoverClose = () => {
    setShowNoMatchingAddress(false);
    setAnchorEl(null);
  };
  const handlePopoverOpen = () => {
    setShowNoMatchingAddress(true);
  };
  const handleSearchAddressChange = (event: SyntheticEvent, value: EnteredAddress | null) => {
    if (!value) return;

    setAddressLookupValue({ address: value, showModal: true });
    setSelectedValue(value);
  };
  const handleSearchInputChange = (event: SyntheticEvent, value: string) => {
    debouncedInputValue(value);
  };
  const handleSelectAddress = (newAddress: EnteredAddress) => {
    updateAddress(undefined);

    setSelectedValue({ ...newAddress });
  };

  const handleValidationRefresh = async () => {
    await refetch();
    closeSnackbar();
  };

  const isValidationDisabled = Object.entries(selectedValue)
    .filter(
      ([k, _]) =>
        k !== 'index' &&
        k !== 'id' &&
        k !== 'street2' &&
        k !== 'locationName' &&
        k !== 'manualEntry' &&
        k !== 'careOf' &&
        k !== 'isAddressValid',
    )
    .some(([_, v]) => !v);

  return (
    <>
      <Grid sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Typography variant='bodySmall' fontWeight='bold'>
          {isSpecialEvent ? 'Customer Address' : 'Business Address'}
        </Typography>
      </Grid>
      <Autocomplete
        fullWidth
        disabled={disabled}
        options={addressSuggestions}
        filterOptions={(options) => options}
        getOptionLabel={(v) => formatAddress(v as EnteredAddress)}
        value={null}
        disableClearable={!!selectedValue}
        forcePopupIcon={false}
        onChange={(e, v) => handleSearchAddressChange(e, v as EnteredAddress | null)}
        onInputChange={handleSearchInputChange} // this needs to change???
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            placeholder='Search Addresses'
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: <Search width='14px' height='14px' style={{ marginRight: '10px' }} />,
              },
            }}
          />
        )}
        renderOption={(props, option: EnteredAddress) => (
          <li {...props} key={props.key}>
            <TypographyHighlighter
              text={formatAddress(option)}
              variant='bodySmall'
              color='common.black'
              highlightText={inputValue}
              highlightStyle={{
                fontWeight: 700,
                backgroundColor: 'transparent',
              }}
            />
          </li>
        )}
        sx={{
          '@media(max-width: 899px)': { maxWidth: '380px' },
        }}
      />
      <Grid sx={{ mt: 2 }}>
        <TextField
          disabled={disabled}
          inputLabel='Street 1'
          name='street1'
          slotProps={{
            input: {
              endAdornment:
                !isValidationDisabled && !disabled ? (
                  <InputValidation loading={isFetching} error={error} status={validationStatus} />
                ) : null,
            },
          }}
          externalIcon={<ExternalIcon isError={!!error} onRefresh={handleValidationRefresh} />}
          value={selectedValue.street1}
          onChange={(e) => handleBusinessAddressChange(e, 'street1')}
          sx={{ width: '100%' }}
        />
      </Grid>
      <Grid sx={{ mt: 2 }}>
        <TextField
          disabled={disabled}
          inputLabel='Street 2'
          name='street2'
          value={selectedValue?.street2}
          onChange={(e) => handleBusinessAddressChange(e, 'street2')}
          sx={{ width: '100%' }}
        />
      </Grid>
      <Grid
        container
        ref={anchorRef}
        sx={{
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        <Grid size={{ sm: 5, xs: 12 }}>
          <TextField
            fullWidth
            disabled={disabled}
            inputLabel='City'
            sx={{ width: '100%' }}
            name='city'
            value={selectedValue?.city}
            onChange={(e) => handleBusinessAddressChange(e, 'city')}
          />
        </Grid>
        <Grid size={{ sm: 3, xs: 12 }}>
          <Select
            disabled={disabled}
            fullWidth
            inputLabel='State'
            name='state'
            items={states}
            value={selectedValue?.state}
            onChange={(e) => handleBusinessAddressChange(e, 'state')}
          />
        </Grid>
        <Grid size={{ sm: 3, xs: 12 }}>
          <TextField
            disabled={disabled}
            inputLabel='Zip'
            name='zipCode'
            sx={{ width: '100%' }}
            value={selectedValue?.zipCode}
            onChange={(e) => handleBusinessAddressChange(e, 'zipCode')}
          />
        </Grid>
      </Grid>
      {isNewCustomer ? (
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: isMobileOrSmallScreen ? '100%' : '500px',
          }}
        >
          <MessageNoMatching
            isConfirmNewCustomerDisabled={isValidationDisabled || isBusinessTypeMissing}
            // TODO: verify function
            onConfirmNewCustomer={handleNoMatchingAddress}
          />
        </Grid>
      ) : null}
      <NoMatchingAddress
        id='no-matching-address-popover'
        aria-labelledby='use-this-address-button'
        addressOptions={validPhysicalAddress?.items}
        open={showNoMatchingAddress}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 400,
          horizontal: 'center',
        }}
        enteredAddress={{
          ...selectedValue,
        }}
        onAddressConfirm={handleAddressConfirm}
        onPopoverClose={handlePopoverClose}
        onSelectAddress={(address) => handleSelectAddress(address)}
        onInvalidAdress={handleInvalidAddress}
      />
    </>
  );
};
